/**
 * Haptics — thin wrapper around @capacitor/haptics.
 *
 * A no-op on web (Capacitor's own Haptics plugin already no-ops safely
 * outside a native shell, but this file exists so call sites never import
 * @capacitor/haptics directly and never need their own IS_NATIVE_APP check —
 * one place to change the feel later, same reasoning as services/runtime.js).
 *
 * Fired at four moments, per the Phase 4 brief — a draft pick made in Live
 * Draft, a trade verdict returned in Analyse Trade, a waiver claim
 * recommendation accepted, and sign-in success. Every call site should use
 * one of the named helpers below rather than calling `impact()`/`notify()`
 * directly, so the mapping of "what happened" → "what it feels like" lives
 * in exactly one file.
 */

import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'
import { IS_NATIVE_APP } from './runtime'

async function safeRun(fn) {
  if (!IS_NATIVE_APP) return
  try {
    await fn()
  } catch {
    // Haptics are a nicety, never a requirement — a plugin error (e.g. an
    // older device, a simulator with no Taptic Engine) must never surface
    // to the user or interrupt whatever flow triggered it.
  }
}

/** A draft pick was made (yours or the room's) in Live Draft. */
export function hapticDraftPick() {
  return safeRun(() => Haptics.impact({ style: ImpactStyle.Medium }))
}

/** A trade verdict came back in Analyse Trade. */
export function hapticTradeVerdict() {
  return safeRun(() => Haptics.notification({ type: NotificationType.Success }))
}

/** A waiver-wire claim recommendation was accepted. */
export function hapticWaiverClaimAccepted() {
  return safeRun(() => Haptics.impact({ style: ImpactStyle.Light }))
}

/** Sign-in succeeded. */
export function hapticSignInSuccess() {
  return safeRun(() => Haptics.notification({ type: NotificationType.Success }))
}
